import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Divider, MenuItem, MenuList } from "@mui/material";
import { useEffectOnce, useTrackEvent } from "../../common/hooks";
import { NavUtils } from "../../navigation/utils";
import {
	Flags,
	hasCompanyPermission,
	hasPermission,
	isSegFeatureEnabled,
	Permissions,
	SegFlags,
	translate,
} from "../../common/providers";
import { ApiService, GonogoCategoryService } from "../../api";
import {
	CircularLoader,
	CustomButton,
	CustomButtonLink,
	CustomDialog,
	DropDown,
	EmptyState,
	icon,
	IconComponent,
	SideDrawerMain,
	VerticalDivider,
	withWebsocket,
} from "../../common/components";
import { exportView } from "../../navigation";
import { CategoryDrawer, GonogoHeader, GonogoContent, GonogoDetails } from "./components";
import { downloadFile, isNonEmptyArray } from "../../common/utils";
import styles from "./Gonogo.module.css";
import { getCategoriesTree, setCurrentCategory, setIsAnalysisInProgress, setSelectedMode } from "./slice/gonogo-slice";
import { findNode } from "./slice/gonogo-utils";
import dashboardService from "../../api/services/dashboard-service";

const Gonogo = ({ subscribeWS }) => {
	const [openDrawer, setOpenDrawer] = useState(true);
	const cancelTokenSourceRef = useRef(null);
	const anchorRef = useRef();
	const dispatch = useDispatch();
	const categories = useSelector(({ gonogo }) => gonogo?.categories);
	const defaultCurrentCategory = useSelector(({ gonogo }) => gonogo.defaultCurrentCategory);
	const projectId = useSelector(({ context }) => context.project.id);
	const companyId = useSelector(({ context }) => context.company?.id);
	const [totalDocuments, setTotalDocuments] = useState();
	const navigate = useNavigate();
	const location = useLocation();
	const isLoadingCategories = useSelector(({ gonogo }) => gonogo.isLoadingCategories);
	const isAnalysisInProgress = useSelector(({ gonogo }) => gonogo.isAnalysisInProgress);
	const [isDownloading, setIsDownLoading] = useState(false);
	const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
	const [openMenu, setOpenMenu] = useState(false);
	const trackEvent = useTrackEvent();

	const getDefaultCategory = useCallback(() => {
		dispatch(setCurrentCategory(defaultCurrentCategory));
		navigate(NavUtils.goToReviewByCategory({ projectId, categoryId: defaultCurrentCategory.id, attached: false }));
	}, [defaultCurrentCategory, dispatch, navigate, projectId]);

	useEffect(() => {
		const documentTitle = translate("gonogo-title");
		document.title = documentTitle;
		trackEvent("OverviewbyCategory_visit");
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
		// eslint-disable-next-line
	}, []);
	useEffect(() => {
		if (!isAnalysisInProgress) {
			dispatch(getCategoriesTree(cancelTokenSourceRef.current.token));
		}
	}, [dispatch, isAnalysisInProgress]);
	const handleAutomationMessage = useCallback(
		({ reason }) => {
			if (reason === "Action trigger ends") {
				dispatch(setIsAnalysisInProgress(false));
			}
		},
		[dispatch]
	);
	useEffect(() => {
		dispatch(setIsAnalysisInProgress(false));
	}, [dispatch]);
	useEffect(() => {
		subscribeWS(`/topic/synthesis-by-category/projects/${projectId}/refresh`, handleAutomationMessage);
	}, [subscribeWS, projectId, handleAutomationMessage]);
	useEffect(() => {
		setIsLoadingDocuments(true);
		if ((projectId, companyId)) {
			dashboardService
				.getProjectDocuments({ projectId, companyId }, cancelTokenSourceRef.current.token)
				.then((data) => {
					setTotalDocuments(data.totalDocuments);
				})
				.catch((err) => {
					console.error(err);
				})
				.finally(() => setIsLoadingDocuments(false));
		}
	}, [projectId, companyId]);
	useEffectOnce(
		() => {
			const urlCategoryId = parseInt(new URLSearchParams(location.search).get("categoryId"), 10);
			if (!urlCategoryId) {
				getDefaultCategory();
			} else {
				const foundCategory = findNode(urlCategoryId, categories);
				if (foundCategory) {
					dispatch(setCurrentCategory(foundCategory));
				} else {
					getDefaultCategory();
				}
				const urlFlagged = new URLSearchParams(location.search).get("attached");
				if (foundCategory && urlFlagged) {
					try {
						const flagged = JSON.parse(urlFlagged.toLowerCase());
						dispatch(setSelectedMode(flagged));
					} catch (e) {
						console.error(e);
					}
				}
			}
		},
		[defaultCurrentCategory, categories, dispatch, getDefaultCategory, location.search],
		() => !!defaultCurrentCategory
	);

	const handleOpenDrawer = () => setOpenDrawer((prev) => !prev);
	const handleToggelMenu = () => setOpenMenu((prev) => !prev);
	const handleNavigateToProjectCategories = () => {
		navigate(NavUtils.goToProjectCategories(projectId));
	};
	const handleExport = () => {
		setIsDownLoading(true);
		GonogoCategoryService.exportCategories({ projectId }, cancelTokenSourceRef.current.token)
			.then(({ data, filename }) => {
				downloadFile({ data, filename, filetype: "xlsx" });
				trackEvent("OverviewbyCategory_export");
			})
			.catch((err) => {
				console.error(err);
			})
			.finally(() => {
				setIsDownLoading(false);
			});
	};

	const renderWildCard = () =>
		isSegFeatureEnabled(SegFlags.CATEGORY) &&
		hasCompanyPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]) &&
		hasPermission(Permissions.PROJECT_LEADER) && (
			<CustomButton
				ref={anchorRef}
				className={styles.menuBtn}
				color="secondary"
				data-testid="manage.categories.btn"
				variant="outlined"
				onClick={handleToggelMenu}
			>
				<IconComponent color="var(--grey-700)" icon={icon.faEllipsisH} size="sm" />
				<DropDown
					ref={anchorRef}
					className={styles.dropDown}
					open={openMenu}
					position="bottom-end"
					onClose={handleToggelMenu}
				>
					<MenuList className={styles.menuList}>
						<MenuItem className={styles.menuItem} onClick={handleNavigateToProjectCategories}>
							<div className={styles.menuItem__icon}>
								<IconComponent color="var(--grey-600)" icon={icon.faCog} size="2xs" />
							</div>
							<span className={styles.menuItem__text}>{translate("gonogo.manage-thematics")}</span>
						</MenuItem>
						<MenuItem className={styles.menuItem} onClick={handleExport}>
							<div className={styles.menuItem__icon}>
								<IconComponent color="var(--grey-600)" icon={icon.faUpload} size="2xs" />
							</div>
							<span className={styles.menuItem__text}>{translate("gonogo.export-analysis-button")}</span>
						</MenuItem>
					</MenuList>
				</DropDown>
			</CustomButton>
		);
	const accessToProjectCategoryButton = (
		<CustomButtonLink
			color="primary"
			data-testid="accessToCategory.btn"
			to={NavUtils.goToProjectCategories(projectId)}
			variant="contained"
		>
			{translate("gonogo.content.no-category.access-projects-btn")}
		</CustomButtonLink>
	);
	const accessToDocCenterButton = (
		<CustomButtonLink color="primary" to={NavUtils.goToDocumentCenter(projectId)} variant="contained">
			{translate("gonogo.content.no-documents.access-documents-btn")}
		</CustomButtonLink>
	);
	const noDocumentsRender = (
		<EmptyState btn1={accessToDocCenterButton} title={translate("gonogo.content.no-documents.title")} />
	);
	const noCategoryRender = (
		<EmptyState
			btn1={
				hasPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]) &&
				accessToProjectCategoryButton
			}
			title={translate("gonogo.content.no-category.title")}
		/>
	);
	return (
		<>
			{(!isLoadingDocuments && totalDocuments === 0 && (
				<div className={styles.noCategoryContainer}>{noDocumentsRender}</div>
			)) ||
				(isLoadingCategories ? (
					<div className={styles.sideDrawer__loader}>
						<CircularLoader size={45} />
					</div>
				) : (
					!isNonEmptyArray(categories) && <div className={styles.noCategoryContainer}>{noCategoryRender}</div>
				)) || (
					<SideDrawerMain
						className={styles.sideDrawer__main}
						contentClassName={styles.sideDrawer__content}
						DrawerComponent={CategoryDrawer}
						openDrawer={openDrawer}
						wildcard={renderWildCard}
						wildCardClassName={styles.wildcard}
						onOpenDrawer={handleOpenDrawer}
					>
						<GonogoHeader />
						<Divider />
						<div className={styles.content}>
							<GonogoContent />
							<VerticalDivider className={styles.divider} />
							<GonogoDetails />
						</div>
					</SideDrawerMain>
				)}
			<CustomDialog
				loader
				maxWidth="xs"
				open={isDownloading}
				subTitle={translate("gonogo.export.wait-message")}
				title={translate("gonogo.export")}
			/>
		</>
	);
};

export { default as gonogoSlice } from "./slice/gonogo-slice";
export default exportView({
	path: "/projects/:projectId/synthesis-by-category",
	localesPath: "/gonogo/locales",
	component: withWebsocket(Gonogo),
	flag: Flags.SELECT_SYNTHESIS,
	segFlag: [SegFlags.CATEGORY, SegFlags.REVIEW_BY_CATEGORY],
});
